import React from 'react';

import type { StreamId } from '@vlabs/api/artemy/model';
import { CropIcon, DetectionIcon, VideoOnIcon } from '@vlabs/icons';
import { Control } from '@vlabs/uikit';
// ponytail: осознанный импорт роутов приложения — виджет используется только в point3
import { routes } from '@vlapps/point3/src/app/routes';
import { generatePath, Link } from 'react-router-dom';

import { useSourceStreamLastFrameTranslation } from './i18n';
import st from './SourceStreamLastFrame.module.sass';

interface SourceStreamZonesMenu {
  items: { key: string; title: string }[];
  onSelect: (key: string) => void;
}

interface SourceStreamLastFrameProps {
  streamId: StreamId;
  /** Клик по «Настроить зоны ROI/DROI» (открытие модалки/списка ВА — на странице). */
  onConfigureZones?: VoidFunction;
  /** Список ВА потока для выбора (показывается вместо прямого открытия модалки). */
  zonesMenu?: SourceStreamZonesMenu;
  /** Ошибка загрузки ретрансляции — текст под кнопкой настройки зон. */
  zonesError?: boolean;
  /** Подключение ретранслятора в обработке — спиннер на кнопке настройки зон. */
  zonesLoading?: boolean;
}

function SourceStreamLastFrame({
  streamId,
  onConfigureZones = undefined,
  zonesMenu = undefined,
  zonesError = false,
  zonesLoading = false,
}: SourceStreamLastFrameProps): React.JSX.Element {
  const { t } = useSourceStreamLastFrameTranslation();

  return (
    <div className={st.Root}>
      <h6 className={st.Title}>{t('title')}</h6>

      <div className={st.Frame}>
        {/* Фон и оверлей кадра — ассеты макета «Трансляция» (public point3) */}
        <img
          alt=""
          className={st.FrameBackground}
          src="/live-stream.png"
        />
        <img
          alt=""
          className={st.FrameOverlay}
          src="/live-stream.svg"
        />

        <Control.Button
          as={Link}
          className={st.LiveButton}
          data-testid="source-stream-live"
          icon={<VideoOnIcon />}
          size="small"
          to={generatePath(routes.sources.streamLive, { streamId })}
        >
          {t('open_live')}
        </Control.Button>
      </div>

      <div className={st.ZonesWrap}>
        <Control.Button
          className={st.ZonesButton}
          data-testid="source-stream-configure-zones"
          icon={<CropIcon />}
          isLoading={zonesLoading}
          onClick={onConfigureZones}
          variant="outlined"
        >
          {t('configure_zones')}
        </Control.Button>

        {!!zonesMenu && (
          <div
            className={st.ZonesMenu}
            data-testid="source-stream-zones-menu"
          >
            {zonesMenu.items.map((item) => (
              <button
                className={st.ZonesMenuItem}
                data-testid={`source-stream-zones-${item.key}`}
                key={item.key}
                onClick={() => zonesMenu.onSelect(item.key)}
                type="button"
              >
                <DetectionIcon className={st.ZonesMenuItemIcon} />
                <span>{item.title}</span>
              </button>
            ))}
          </div>
        )}

        {!!zonesError && (
          <div
            className={st.ZonesError}
            data-testid="source-stream-zones-error"
          >
            <span className={st.ZonesErrorTitle}>{t('restream_error_title')}</span>
            <span>{t('restream_error_message')}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export { SourceStreamLastFrame };
